import {
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

//Notification
import { BnEsDatePipe } from './components/nav/top-bar/pipelines/bn-es-date.pipe';

import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  HttpClientXsrfModule,
} from '@angular/common/http';
import {
  ItemModel,
  TemplateRegistry,
} from '@backbase/foundation-ang/core';

import { authConfig, environment } from 'src/environments/environment';

import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';

import { AuthModule } from './auth/src/auth.module';
import { IdentityAuthModule } from '@backbase/identity-auth';
import { RemoteConfigService } from '@backbase/remote-config-ang';
import { RetailAppRemoteConfig } from './remote-config/remote-config';
import { AuthInterceptor } from './auth/src/auth.interceptor';

import { BnViewSelectContextModule } from './views/bn-view-select-context/bn-view-select-context.module';
import { EffectsModule } from '@ngrx/effects';
import { EventLoginInterceptor } from './services/EventLoginInterceptor';
import { AppDataModule } from './app-data.module';

//API
import { PaymentBatchConfiguration } from '@backbase/data-ang/payment-batch';
import { PaymentBatchApiModule } from '@backbase/payment-batch-http-ang';
import {
  AccessControlApiModule,
  AccessControlConfiguration,
} from '@backbase/accesscontrol-http-ang';
import { PaymentAuxConfiguration } from 'payment-api';
import {
  PaymentOrderApiModule,
  PaymentOrderConfiguration,
} from '@backbase/data-ang/payment-order';
import { LimitApiModule, LimitConfiguration } from '@backbase/limit-http-ang';
import {
  ContactManagerApiModule,
  ContactManagerConfiguration,
} from '@backbase/data-ang/contact-manager';
import { AuditConfiguration, AuditApiModule } from '@backbase/audit-http-ang';
import {
  ArrangementManagerApiModule,
  ArrangementManagerConfiguration,
} from '@backbase/arrangement-manager-http-ang';
import {
  TransactionsApiModule,
  TransactionsConfiguration,
} from '@backbase/transactions-http-ang';
import { ActivatedRoute } from '@angular/router';
import { ManagePaymentsListInterceptor } from './services/bn-manage-payments-list.interceptor';
import { PaymentAuxApiModule } from 'payment-api';
import { BnAproveContactRequestInterceptor } from './services/bn-approve-contact-request-interceptor';
import { ContactPayeeGroupInterceptor } from './interceptors/contactPayeeGroupInterceptor';
import { BusinessFunctionInterceptor } from './services/BusinessFunctionInterceptor';


//Menu
import { ProgressTrackerWidgetModule } from '@backbase/progress-tracker-widget-ang';
import { ProgressbarModule } from '@backbase/ui-ang';
import { NotificationsBadgeWidgetModule } from '@backbase/notifications-badge-widget-ang';
import {
  BUSINESS_FUNCTION_BANESCO,
  BusinessFunctionBanesco
} from 'ui-library';
import { SummaryInterceptorService } from './services/SummaryInterceptor.service';
import { BnEsSimpleDatePipeModule } from 'ui-library-pipes';
import { BnSetLocaleWidgetCustomModule } from 'bn-set-locale-widget-custom';
import { BnTransactionsTableInterceptor } from './interceptors/bn-transactions-table.interceptor';
import { BnTransactionsDeleteIdsInterceptor } from './interceptors/bn-transactions-delete-ids';
import { ManageUsersPermisionsInterceptor } from './services/ManageUsersPermisionsInterceptor';
import { JobRoleCreateRedirectInterceptor } from './interceptors/job-role-create-redirect.interceptor';
import { WorkflowBusinessFunctionsListInterceptor } from './interceptors/workflow-business-functions-list.interceptor';

function journeyItemModelFactory(route) {
  return route.snapshot.data.item;
}

export function applicationInitializer(
  remoteConfig: RemoteConfigService<RetailAppRemoteConfig>
) {
  return () => remoteConfig.fetchAndActivate();
}

const auditLogConfiguration = () =>
  new AuditConfiguration({ basePath: '/api/audit-service' });
const paymentAuxConfiguration = () =>
  new PaymentAuxConfiguration({
    basePath: '/api/payment-order-aux-integration-service',
  });

const transactionsConfiguration = () =>
  new TransactionsConfiguration({ basePath: '/api/transaction-manager' });
const LimitConfigurations = () =>
  new LimitConfiguration({ basePath: '/api/limit' });
const arrangementManagerConfiguration = () =>
  new ArrangementManagerConfiguration({ basePath: '/api/arrangement-manager' });
const contactManagerConfiguration = () =>
  new ContactManagerConfiguration({ basePath: '/api/contact-manager' });
const paymentOrderConfiguration = () =>
  new PaymentOrderConfiguration({ basePath: '/api/payment-order-service' });
const batchesConfiguration = () =>
  new PaymentBatchConfiguration({ basePath: '/api/batch-service' });
const accessControlConfiguration = () =>
  new AccessControlConfiguration({ basePath: '/api/access-control' });

@NgModule({
  declarations: [
    AppComponent,
    BnEsDatePipe,


  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    HttpClientXsrfModule,
    IdentityAuthModule,
    environment.animation ? BrowserAnimationsModule : NoopAnimationsModule,

    AuthModule.forRoot(environment.apiRoot, authConfig),
    AppDataModule,
    AppRoutingModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    BnViewSelectContextModule,

    AuditApiModule.forRoot(auditLogConfiguration),
    PaymentAuxApiModule.forRoot(paymentAuxConfiguration),
    TransactionsApiModule.forRoot(transactionsConfiguration),
    AccessControlApiModule.forRoot(accessControlConfiguration),
    ArrangementManagerApiModule.forRoot(arrangementManagerConfiguration),
    LimitApiModule.forRoot(LimitConfigurations),
    PaymentOrderApiModule.forRoot(paymentOrderConfiguration),
    ContactManagerApiModule.forRoot(contactManagerConfiguration),
    PaymentBatchApiModule.forRoot(batchesConfiguration),

    BnSetLocaleWidgetCustomModule.forRoot({
      locales: environment.locales,
    }),
    ProgressbarModule
  ],
  providers: [
    ...(environment.mockProviders || []),
    { 
      provide: HTTP_INTERCEPTORS, 
      useClass: JobRoleCreateRedirectInterceptor, 
      multi: true 
    },
    { 
      provide: HTTP_INTERCEPTORS, 
      useClass: WorkflowBusinessFunctionsListInterceptor, 
      multi: true 
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ManageUsersPermisionsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ManageUsersPermisionsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BnTransactionsTableInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BnTransactionsDeleteIdsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ContactPayeeGroupInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BusinessFunctionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ManagePaymentsListInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BnAproveContactRequestInterceptor,
      multi: true,
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: SummaryInterceptorService,
    //   multi: true,
    // },
    {
      provide: ItemModel,
      useFactory: journeyItemModelFactory,
      deps: [ActivatedRoute],
    },
    TemplateRegistry,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EventLoginInterceptor,
      multi: true,
    },
    BusinessFunctionBanesco,
    {
      provide: BUSINESS_FUNCTION_BANESCO,
      useValue: [
        '1016',
        '1037',
        'banescoDR.1014',
        'banescoDR.1015',
        'banescoDR.1016',
        'banescoDR.1017',
        'banescoDR.1018',
        'banescoDR.1019',
        'banescoDR.1020',
        'banescoDR.1021',
        'banescoDR.1022',
        'banescoDR.1023',
        'banescoDR.1024',
        'banescoDR.1025',
        '1006',
        '1003',
        '1005',
        '1032',
        '1033',
        '1013',
        '1019',
        '1020',
        '1011',
        '1012',
        '1040',
        '1007',
        '1009',
        '1028',
        '1010',
        '1078',
        '1035',
        '1051',
        '1066',
        '1034',
        '1049',
        '1050',
        '1084',
        '1030',
        '1119'
      ],
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
